export type MentionEntityType = 'character' | 'location' | 'document';

export interface EntityMentionOption {
  id: string;
  name: string;
}

export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function extractMentionedEntityIds(html: string, entityType: MentionEntityType): string[] {
  if (!html) {
    return [];
  }
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const spans = doc.querySelectorAll(`span[data-type="mention"][data-entity-type="${entityType}"]`);
  return Array.from(spans)
    .map((el) => el.getAttribute('data-id'))
    .filter((id): id is string => Boolean(id));
}

export function buildHiddenMentionsHtml(
  entityType: MentionEntityType,
  ids: string[],
  optionsById: Map<string, EntityMentionOption>
): string {
  const spans = ids
    .map((id) => optionsById.get(id))
    .filter((option): option is EntityMentionOption => Boolean(option))
    .map((option) => {
      const safeLabel = escapeHtml(option.name);
      return `<span data-type="mention" data-id="${option.id}" data-entity-type="${entityType}" data-label="${safeLabel}">@${safeLabel}</span>`;
    });
  return spans.join(' ');
}

const HIDDEN_LINKED_ENTITIES_SELECTOR = '[data-resource-linked-entities="true"]';

export function stripHiddenLinkedEntitiesBlock(html: string): string {
  if (!html) {
    return html;
  }
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.querySelectorAll(HIDDEN_LINKED_ENTITIES_SELECTOR).forEach((el) => el.remove());
  return doc.body.innerHTML;
}

export function withLinkedEntityMentions(
  visibleHtml: string,
  characterIds: string[],
  locationIds: string[],
  characterOptionsById: Map<string, EntityMentionOption>,
  locationOptionsById: Map<string, EntityMentionOption>
): string {
  const contentCharacterMentionIds = new Set(extractMentionedEntityIds(visibleHtml, 'character'));
  const contentLocationMentionIds = new Set(extractMentionedEntityIds(visibleHtml, 'location'));
  const characterMentions = buildHiddenMentionsHtml(
    'character',
    characterIds.filter((id) => !contentCharacterMentionIds.has(id)),
    characterOptionsById
  );
  const locationMentions = buildHiddenMentionsHtml(
    'location',
    locationIds.filter((id) => !contentLocationMentionIds.has(id)),
    locationOptionsById
  );
  const hiddenMentions = [characterMentions, locationMentions].filter(Boolean).join(' ');
  const base = visibleHtml.trim();
  return hiddenMentions
    ? `${base}<p style="display:none" data-resource-linked-entities="true">${hiddenMentions}</p>`
    : base;
}
